import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@livable/saas-react-components';
import { Box, Typography } from '@mui/material';
import { subMonths } from 'date-fns';
import { formatProseDate, parseScheduleDate } from '../scheduleChangeDates';
import { getScheduleTimelineSummary } from '../scheduleTimeline';
import useStyles from './styles';
const KEY_PREFIX = 'subscriptions.scheduleChange.cancel.callout';
const COUNT_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const ORDINAL_SUFFIXES = {
  1: 'st',
  2: 'nd',
  3: 'rd',
  21: 'st',
  22: 'nd',
  23: 'rd',
  31: 'st'
};
const formatMoney = amount => "$".concat(amount.toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}));
const toCountWord = count => {
  var _COUNT_WORDS$count;
  return (_COUNT_WORDS$count = COUNT_WORDS[count]) !== null && _COUNT_WORDS$count !== void 0 ? _COUNT_WORDS$count : String(count);
};
const toCapitalizedCountWord = count => {
  const word = toCountWord(count);
  return word.charAt(0).toUpperCase() + word.slice(1);
};
const countMonthsLeftInTerm = (commitmentEnd, termMonths) => {
  if (!commitmentEnd) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let months = 0;
  let monthEnd = commitmentEnd;
  while (monthEnd >= today && months < (termMonths || 24)) {
    months += 1;
    monthEnd = subMonths(monthEnd, 1);
  }
  return months;
};
const toOrdinalDay = date => {
  var _parseScheduleDate, _ORDINAL_SUFFIXES$day;
  const day = (_parseScheduleDate = parseScheduleDate(date)) === null || _parseScheduleDate === void 0 ? void 0 : _parseScheduleDate.getDate();
  if (!day) return '';
  return "".concat(day).concat((_ORDINAL_SUFFIXES$day = ORDINAL_SUFFIXES[day]) !== null && _ORDINAL_SUFFIXES$day !== void 0 ? _ORDINAL_SUFFIXES$day : 'th');
};
const toDateList = dates => {
  const formatted = dates.map(date => formatProseDate(date)).filter(Boolean);
  if (formatted.length <= 1) return formatted.join('');
  return "".concat(formatted.slice(0, -1).join(', '), " and ").concat(formatted[formatted.length - 1]);
};
export const CancelCallout = _ref => {
  var _estimate$timeline, _finalNode$coveredMon, _estimate$timeline2, _invoiceDates, _finalNode$amount;
  let estimate = _ref.estimate,
    termMonths = _ref.termMonths,
    _ref$isMonthly = _ref.isMonthly,
    isMonthly = _ref$isMonthly === void 0 ? false : _ref$isMonthly,
    activeUnits = _ref.activeUnits,
    commitmentEndDate = _ref.commitmentEndDate;
  const classes = useStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const summary = getScheduleTimelineSummary((_estimate$timeline = estimate.timeline) !== null && _estimate$timeline !== void 0 ? _estimate$timeline : []);
  const finalNode = summary.finalNode;
  /** Ops settled this charge themselves, so there are no covered months to account for it. */
  const isManualCharge = typeof estimate.earlyTerminationAmount === 'number';
  const impactMonthCount = ((_finalNode$coveredMon = finalNode === null || finalNode === void 0 ? void 0 : finalNode.coveredMonths) !== null && _finalNode$coveredMon !== void 0 ? _finalNode$coveredMon : []).filter(month => month >= estimate.effectiveDate).length;
  const invoiceDates = ((_estimate$timeline2 = estimate.timeline) !== null && _estimate$timeline2 !== void 0 ? _estimate$timeline2 : []).map(node => node.date).sort();
  const invoiceCount = invoiceDates.length;
  const lastDate = (_invoiceDates = invoiceDates[invoiceCount - 1]) !== null && _invoiceDates !== void 0 ? _invoiceDates : null;
  const commitmentEnd = parseScheduleDate(commitmentEndDate);
  const effective = parseScheduleDate(estimate.effectiveDate);
  const dayAfterCommitmentEnd = commitmentEnd ? new Date(commitmentEnd.getFullYear(), commitmentEnd.getMonth(), commitmentEnd.getDate() + 1) : null;
  const isFullTerm = !isMonthly && !!effective && !!dayAfterCommitmentEnd && effective.getTime() === dayAfterCommitmentEnd.getTime();
  const isNoCharge = !isMonthly && !isFullTerm && ((_finalNode$amount = finalNode === null || finalNode === void 0 ? void 0 : finalNode.amount) !== null && _finalNode$amount !== void 0 ? _finalNode$amount : 0) === 0 && (activeUnits !== null && activeUnits !== void 0 ? activeUnits : 0) > 0;
  const title = (_finalNode$amount3 => {
    if (isFullTerm) return t("".concat(KEY_PREFIX, ".full_term_title"));
    if (isNoCharge) return t("".concat(KEY_PREFIX, ".no_charge_title"));
    if (!isMonthly && impactMonthCount === 1) {
      var _finalNode$amount2;
      return t("".concat(KEY_PREFIX, ".remaining_commitment_title"), {
        amount: formatMoney((_finalNode$amount2 = finalNode === null || finalNode === void 0 ? void 0 : finalNode.amount) !== null && _finalNode$amount2 !== void 0 ? _finalNode$amount2 : 0)
      });
    }
    if (isMonthly) {
      return t(invoiceCount > 0 ? "".concat(KEY_PREFIX, ".impact_title") : "".concat(KEY_PREFIX, ".impact_title_none"), {
        count: invoiceCount
      });
    }
    return t("".concat(KEY_PREFIX, ".charge_title"), {
      amount: formatMoney((_finalNode$amount3 = finalNode === null || finalNode === void 0 ? void 0 : finalNode.amount) !== null && _finalNode$amount3 !== void 0 ? _finalNode$amount3 : 0)
    });
  })();
  const consequence = (() => {
    if (isMonthly && invoiceCount === 1) {
      return t("".concat(KEY_PREFIX, ".monthly_single"), {
        date: formatProseDate(lastDate)
      });
    }
    if (isMonthly && invoiceCount > 1 && invoiceCount <= 3) {
      return t("".concat(KEY_PREFIX, ".monthly_list"), {
        count: invoiceCount - 1,
        countWord: toCapitalizedCountWord(invoiceCount - 1),
        dates: toDateList(invoiceDates.slice(0, -1)),
        finalDate: formatProseDate(lastDate)
      });
    }
    if (isMonthly && invoiceCount > 3) {
      return t("".concat(KEY_PREFIX, ".monthly_many"), {
        dayOrdinal: toOrdinalDay(invoiceDates[0]),
        secondToLastDate: formatProseDate(invoiceDates[invoiceCount - 2]),
        finalDate: formatProseDate(lastDate)
      });
    }
    if (!isMonthly) {
      var _finalNode$amount4;
      const monthsLeft = countMonthsLeftInTerm(parseScheduleDate(commitmentEndDate), termMonths);
      const isZeroChargeWithoutUnits = ((_finalNode$amount4 = finalNode === null || finalNode === void 0 ? void 0 : finalNode.amount) !== null && _finalNode$amount4 !== void 0 ? _finalNode$amount4 : 0) === 0 && activeUnits === 0;
      const canNameBillDate = !!finalNode && impactMonthCount > 0;
      const detailKey = (() => {
        if (isZeroChargeWithoutUnits) return 'charge_detail_no_units';
        if (!canNameBillDate) return 'charge_detail_unbilled';
        return 'charge_detail';
      })();
      const commitment = monthsLeft ? t("".concat(KEY_PREFIX, ".").concat(detailKey), {
        count: monthsLeft,
        termMonths: termMonths !== null && termMonths !== void 0 ? termMonths : '',
        endDate: formatProseDate(estimate.effectiveDate)
      }) : '';

      // A charge set by hand names no months, so say where the figure came from and when it is billed
      // rather than falling through to the wording for one nobody has worked out yet.
      if (isManualCharge && finalNode) {
        const manual = t("".concat(KEY_PREFIX, ".charge_detail_manual"), {
          endDate: formatProseDate(estimate.effectiveDate),
          billDate: formatProseDate(finalNode.date)
        });
        const left = monthsLeft ? t("".concat(KEY_PREFIX, ".charge_detail"), {
          count: monthsLeft,
          termMonths: termMonths !== null && termMonths !== void 0 ? termMonths : ''
        }) : '';
        return [left, manual].filter(Boolean).join(' ');
      }
      if (isZeroChargeWithoutUnits || !canNameBillDate) return commitment;
      const billKey = impactMonthCount > 1 && commitmentEndDate ? 'charge_detail_bill_through' : 'charge_detail_bill';
      const billing = t("".concat(KEY_PREFIX, ".").concat(billKey), {
        count: impactMonthCount,
        endDate: formatProseDate(estimate.effectiveDate),
        termEndDate: formatProseDate(commitmentEndDate),
        billDate: formatProseDate(finalNode.date)
      });
      return [commitment, billing].filter(Boolean).join(' ');
    }
    return '';
  })();
  const serviceEnd = t("".concat(KEY_PREFIX, ".service_end"), {
    date: formatProseDate(estimate.effectiveDate)
  });
  const fullTermDetail = t("".concat(KEY_PREFIX, ".full_term_detail"), {
    endDate: formatProseDate(commitmentEndDate),
    termMonths: termMonths !== null && termMonths !== void 0 ? termMonths : ''
  });
  const body = (() => {
    if (isFullTerm) return fullTermDetail;
    if (isNoCharge) return t("".concat(KEY_PREFIX, ".no_charge_detail"), {
      endDate: formatProseDate(estimate.effectiveDate)
    });
    if (isMonthly) return [serviceEnd, consequence].filter(Boolean).join(' ');
    return consequence || serviceEnd;
  })();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    className: "".concat(classes.callout, " ").concat(isMonthly || isFullTerm || isNoCharge ? classes.calloutImpact : classes.calloutCharge)
  }, /*#__PURE__*/React.createElement(Icon, {
    type: isMonthly || isFullTerm || isNoCharge ? 'info' : 'alert'
  }), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    className: classes.calloutTitle
  }, title), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.calloutLine
  }, body))), estimate.hasUsablePaymentMethod === false && /*#__PURE__*/React.createElement(Box, {
    className: "".concat(classes.callout, " ").concat(classes.calloutError)
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "error"
  }), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    className: classes.calloutTitle
  }, t("".concat(KEY_PREFIX, ".no_usable_payment_method_title"))), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.calloutLine
  }, t("".concat(KEY_PREFIX, ".no_usable_payment_method_detail"))))));
};